import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import Button from "@components/Button";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import { useLang } from "@src/hooks/useLang";

interface IProps {
  visible: boolean;
  mode: "create" | "rename";
  initialName?: string;
  error?: string | null;
  onClose?: () => void;
  onSubmit: (name: string) => void;
  onDelete?: () => void;
}

const DeleteButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  align-self: center;
  font-family: Nunito Sans, sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #e05252;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FarmDialog: React.FC<IProps> = ({
  visible,
  mode,
  initialName,
  error,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const lang = useLang();
  const [name, setName] = useState(initialName ?? "");

  // Reset the field whenever the dialog opens for a different farm/mode.
  useEffect(() => {
    if (visible) setName(initialName ?? "");
  }, [visible, initialName]);

  const disabled = name.trim().length === 0;

  return (
    <Dialog
      style={{ width: 336 }}
      visible={visible}
      onClose={onClose}
      error={error}
      title={mode === "create" ? lang.farms.createTitle : lang.farms.renameTitle}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        label={lang.farms.nameLabel}
        placeholder={lang.farms.namePlaceholder}
      />
      <SizedBox height={24} />
      <Row>
        <Button kind="outline" onClick={onClose}>
          {lang.farms.cancel}
        </Button>
        <SizedBox width={16} />
        <Button onClick={() => onSubmit(name.trim())} disabled={disabled}>
          {mode === "create" ? lang.farms.create : lang.farms.save}
        </Button>
      </Row>
      {mode === "rename" && onDelete && (
        <>
          <SizedBox height={16} />
          <DeleteButton onClick={onDelete}>{lang.farms.delete}</DeleteButton>
        </>
      )}
    </Dialog>
  );
};

export default observer(FarmDialog);
